import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

axios.defaults.withCredentials = true;

const logger = {
  warn: (...args) => process.env.NODE_ENV !== 'production' && console.warn('[Auth]', ...args),
  error: (...args) => process.env.NODE_ENV !== 'production' && console.error('[Auth]', ...args)
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function getAuthHeaders() {
  const token = sessionStorage.getItem('auth_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function persistToken(token) {
  sessionStorage.setItem('auth_token', token);
  localStorage.setItem('token', token);
}

function clearToken() {
  sessionStorage.removeItem('auth_token');
  localStorage.removeItem('token');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        setUser(false);
        setLoading(false);
        return;
      }
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        headers,
        withCredentials: true
      });
      setUser(data);
    } catch (_err) {
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      persistToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const register = useCallback(async (email, password, name, phone = null) => {
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, phone },
        { withCredentials: true }
      );
      persistToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const registerProfessional = useCallback(async (profData) => {
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/professionals/register`,
        profData,
        { withCredentials: true }
      );
      persistToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      persistToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      logger.warn('Logout request failed:', err.message);
    } finally {
      clearToken();
      setUser(false);
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/users/profile`,
        profileData,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setUser(prev => ({ ...prev, ...data }));
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: errorMsg };
    }
  }, []);

  const authMutations = useMemo(() => ({
    login, register, registerProfessional, loginWithGoogle
  }), [login, register, registerProfessional, loginWithGoogle]);

  const authLifecycle = useMemo(() => ({
    logout, updateProfile, checkAuth
  }), [logout, updateProfile, checkAuth]);

  const authState = useMemo(() => ({
    user, loading, error
  }), [user, loading, error]);

  const contextValue = useMemo(() => ({
    ...authState,
    ...authMutations,
    ...authLifecycle
  }), [authState, authMutations, authLifecycle]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export { getAuthHeaders };

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
